// src/pages/health/TeamActivity.jsx
// D-297 Adoption Health Dashboard — "Team Activity", Owner only.
//
// WHY THIS EXISTS: the Owner needs to see, without asking anyone, who is
// really using Zeno. One row per user: the last meaningful action in the last
// 7 days and a colour. "Active" = a real action (MRF, issue, PO, GRN, bill,
// payment, expense, job ...), never a login.
// v1.1 (Ravi 4 Nov): the colour depends on the ROLE and counts WORKING days
// (Sundays not counted) — ROLE_BANDS in activityMap.js; "Inactive" only for
// users switched off; a WhatsApp nudge on amber / red rows and for "Never
// logged in" — it only opens the Owner's own WhatsApp with a filled message,
// signed by the Owner and the company, never as Zeno.
// Commit 5 (Ravi 4 Nov): the page follows the app-wide Hinglish / English
// toggle (useLang, D-307) — English until someone chooses; the WhatsApp text
// stays Roman Hinglish. The chips are filters (tap = only that group, tap again
// = all).
//
// Display only. Which collections / fields count, who did it, the colours, the
// labels, the legend and the filters come ONLY from src/utils/activityMap.js;
// the page texts from src/utils/teamActivityText.js; the message and the link
// from src/utils/nudgeMessage.js (all pure + Jest); the live reads from
// src/services/activityFeedService.js. Nothing here writes Firestore.
// Live: one listener per source + users; "Refresh now" reopens them with a new
// 7-day window; a 1-minute timer recolours from the loaded data (no reads).
// A source that failed or hit its 500 limit is named in a banner — never hidden.

import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  ACTIVITY_SOURCES, BAND, FILTERS, QUERY_LIMIT, WINDOW_DAYS,
  buildActivityRows, collectActions, filterView, groupLabel, legendFor, limitHit,
  scoreCounts, toggleFilter, whenText, windowStart, workingDaysAgo,
} from "../../utils/activityMap";
import { noPhoneHint, teamText } from "../../utils/teamActivityText";
import { NUDGE_KIND, contactPhoneOf, nudgeLink, pickCompanyName } from "../../utils/nudgeMessage";
import { subscribeActivity, subscribeCompanyUsers, waitForCompanyClaim, writeCountsCache } from "../../services/activityFeedService";
import { roleLabel } from "../../utils/userRoles";
import { useCompany } from "../../context/CompanyContext";
import { useLang } from "../../i18n/useLang";
import LangToggle from "../../i18n/LangToggle";

const PAGE_DEFAULT_LANG = "en"; // English until someone presses the toggle (Ravi 4 Nov)

// What each source is, in words (banners only; kept English in both languages).
const SOURCE_LABELS = {
  service_logs:          "Service jobs",
  mrf_created:           "MRFs raised",
  mrf_updated:           "MRF approvals",
  material_issues:       "Stock issues",
  material_returns:      "Material returns",
  purchase_requests:     "Purchase requests",
  purchase_orders:       "Purchase orders",
  purchase_receipts:     "GRN / Direct Site finalize",
  purchase_bills:        "Purchase bills",
  payments:              "Payments",
  expenses_created:      "Expenses submitted",
  expenses_updated:      "Expense approvals",
  subcontractor_logs:    "Subcontractor confirmations",
  invoices:              "Invoices",
  adjustments_created:   "Stock adjustments submitted",
  adjustments_updated:   "Stock adjustment approvals",
  item_requests_created: "Item requests",
  item_requests_updated: "Item request decisions",
};

const BAND_STYLE = {
  [BAND.GREEN]: { dot: "bg-green-500", text: "text-green-800" },
  [BAND.AMBER]: { dot: "bg-amber-500", text: "text-amber-800" },
  [BAND.RED]:   { dot: "bg-red-500",   text: "text-red-800" },
};

// Chip looks: normal and pressed (the selected filter).
const CHIP_STYLE = {
  [BAND.RED]:   { idle: "bg-red-50 border-red-200 text-red-800",       on: "bg-red-600 border-red-700 text-white" },
  [BAND.AMBER]: { idle: "bg-amber-50 border-amber-200 text-amber-800", on: "bg-amber-500 border-amber-600 text-white" },
  [BAND.GREEN]: { idle: "bg-green-50 border-green-200 text-green-800", on: "bg-green-600 border-green-700 text-white" },
  never:        { idle: "bg-gray-50 border-gray-200 text-gray-700",    on: "bg-gray-700 border-gray-800 text-white" },
  inactive:     { idle: "bg-gray-50 border-gray-200 text-gray-500",    on: "bg-gray-500 border-gray-600 text-white" },
};

const errorKey = (err) => {
  if (err?.code === "failed-precondition") return "errIndex";
  if (err?.code === "permission-denied") return "errPermission";
  return "errOther";
};

const dateTime = (ms) =>
  new Date(ms).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false });

const hhmm = (ms) =>
  new Date(ms).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false });

// A broken message must never break the page: no link = no button.
const safeNudge = (args) => {
  try {
    return nudgeLink(args);
  } catch (err) {
    console.warn("[TeamActivity] nudge not built:", err);
    return null;
  }
};

function WhatsAppButton({ nudge, name, tx }) {
  if (!nudge) return null;
  return (
    <a
      href={nudge.href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`${tx("whatsapp")} ${name || ""} — ${tx("whatsappTitle")}`}
      title={tx("whatsappTitle")}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border border-green-300 bg-green-50 text-green-800 hover:bg-green-100 whitespace-nowrap"
    >
      <span aria-hidden="true">💬</span> {tx("whatsapp")}
    </a>
  );
}

// Commit 6: no valid mobile → a hint instead of the WhatsApp button. Only a
// technician gets the "add number →" link (Edit User has a phone box only for
// technicians); other roles get the text alone.
function NoPhoneHint({ hint, tx }) {
  if (!hint) return null;
  if (hint.href) {
    return (
      <Link to={hint.href} className="inline-flex items-center px-3 py-1.5 text-xs font-semibold rounded-lg border border-gray-300 bg-white text-indigo-700 hover:bg-gray-50 whitespace-nowrap">
        {tx(hint.key)}
      </Link>
    );
  }
  return <span className="inline-flex items-center px-1 py-1.5 text-xs text-gray-500 whitespace-nowrap">{tx(hint.key)}</span>;
}

function ActionLine({ a }) {
  return (
    <span className="break-words">
      <span className="font-medium text-gray-800">{a.label}</span>
      {a.ref ? <span className="text-gray-500"> · {a.ref}</span> : null}
    </span>
  );
}

function RecentList({ recent, nowMs, lang, tx }) {
  if (!recent.length) {
    return <p className="text-xs text-gray-500">{tx("recentEmpty", { days: WINDOW_DAYS })}</p>;
  }
  return (
    <ol className="space-y-1.5">
      {recent.map((a) => (
        <li key={a.key} className="text-xs flex flex-wrap gap-x-2 gap-y-0.5">
          <span className="text-gray-400 whitespace-nowrap">{dateTime(a.atMs)}</span>
          <ActionLine a={a} />
          <span className="text-gray-400">({whenText(a.atMs, nowMs, lang)})</span>
        </li>
      ))}
    </ol>
  );
}

function UserRow({ row, nowMs, open, onToggle, nudge, phoneHint, lang, tx }) {
  const style = BAND_STYLE[row.band];
  return (
    <div className="bg-white border border-gray-200 rounded-xl">
      {/* The toggle and the WhatsApp link are siblings — a link cannot sit inside a button. */}
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="w-full text-left p-3 sm:p-4 flex items-start gap-3"
      >
        <span className={`mt-1.5 h-3 w-3 rounded-full shrink-0 ${style.dot}`} aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-2">
            <span className="text-sm font-semibold text-gray-900 break-words">{row.user.name || tx("noName")}</span>
            <span className="text-xs text-gray-500">{roleLabel(row.user.role)}</span>
          </div>
          <div className="text-xs mt-0.5">
            {row.last ? <ActionLine a={row.last} /> : <span className="text-gray-500">{tx("noActionRecorded")}</span>}
          </div>
        </div>
        <div className="text-right shrink-0 max-w-[45%]">
          <p className={`text-xs font-semibold ${style.text}`}>{groupLabel(row.band, lang)}</p>
          <p className="text-xs text-gray-500">{row.whenText}</p>
        </div>
      </button>
      {(nudge || phoneHint) && (
        <div className="px-3 sm:px-4 pb-3 -mt-1 flex justify-end">
          {nudge ? <WhatsAppButton nudge={nudge} name={row.user.name} tx={tx} /> : <NoPhoneHint hint={phoneHint} tx={tx} />}
        </div>
      )}
      {open && (
        <div className="px-3 sm:px-4 pb-3 sm:pb-4 pt-0 border-t border-gray-100">
          <p className="text-[11px] uppercase tracking-wide text-gray-400 mt-2 mb-1.5">{tx("recentTitle", { days: WINDOW_DAYS })}</p>
          <RecentList recent={row.recent} nowMs={nowMs} lang={lang} tx={tx} />
        </div>
      )}
    </div>
  );
}

function FilterChip({ id, label, count, selected, onTap }) {
  const s = CHIP_STYLE[id];
  return (
    <button
      type="button"
      onClick={() => onTap(id)}
      aria-pressed={selected}
      className={`min-h-[32px] text-xs font-semibold px-3 py-1.5 rounded-full border transition ${
        selected ? `${s.on} ring-2 ring-offset-1 ring-gray-400 shadow-inner` : `${s.idle} hover:brightness-95`
      }`}
    >
      {label} {count}
    </button>
  );
}

export default function TeamActivity() {
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const companyId = user.company_id || "";
  const isOwner = user.role === "owner";
  const { company } = useCompany();
  const { lang } = useLang(null, { defaultLang: PAGE_DEFAULT_LANG });
  const tx = (key, vars) => teamText(key, lang, vars);
  const appUrl = typeof window !== "undefined" ? window.location.origin : "";
  // Company doc's name, or the logged-in user's when missing / "My Company" placeholder.
  const sender = { senderName: user.name || "", companyName: pickCompanyName(company?.company_name, user.company_name) };

  const [epoch, setEpoch] = useState(0);              // "Refresh now" bumps this
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [users, setUsers] = useState(null);
  const [usersError, setUsersError] = useState(null);
  const [docsBySource, setDocsBySource] = useState({});
  const [sizes, setSizes] = useState({});
  const [errors, setErrors] = useState({});
  const [claimMissing, setClaimMissing] = useState(false);
  const [lastChangeAt, setLastChangeAt] = useState(null);
  const [openKey, setOpenKey] = useState(null);
  // Chip filter: null = all. Commit 7: the Home card opens /team-activity?filter=remind
  // (red rows + Never logged in, without you) — read once when the page opens;
  // an unknown value = all.
  const [searchParams] = useSearchParams();
  const [filter, setFilter] = useState(() => {
    const f = searchParams.get("filter");
    return FILTERS.includes(f) ? f : null;
  });
  const viewerUid = user.uid || "";
  const viewerDocId = user.docId || "";

  // Live listeners — reopened on Refresh now with a fresh 7-day window.
  useEffect(() => {
    if (!companyId || !isOwner) return undefined;
    let cancelled = false;
    let closeActivity = null;
    let closeUsers = null;

    setUsers(null);
    setUsersError(null);
    setDocsBySource({});
    setSizes({});
    setErrors({});
    setClaimMissing(false);
    const opened = Date.now();
    setNowMs(opened);

    (async () => {
      const ok = await waitForCompanyClaim(companyId);
      if (cancelled) return;
      if (!ok) { setClaimMissing(true); return; }

      closeUsers = subscribeCompanyUsers({
        companyId,
        onUsers: (list) => { setUsers(list); setUsersError(null); setLastChangeAt(Date.now()); },
        onError: (err) => { console.error("[TeamActivity] users:", err); setUsersError(err); },
      });
      closeActivity = subscribeActivity({
        companyId,
        sinceMs: windowStart(opened),
        onSource: (sourceId, docs, size) => {
          setDocsBySource((prev) => ({ ...prev, [sourceId]: docs }));
          setSizes((prev) => ({ ...prev, [sourceId]: size }));
          setErrors((prev) => {
            if (!prev[sourceId]) return prev;
            const next = { ...prev };
            delete next[sourceId];
            return next;
          });
          setLastChangeAt(Date.now());
        },
        onError: (sourceId, err) => {
          console.error(`[TeamActivity] ${sourceId}:`, err);
          setErrors((prev) => ({ ...prev, [sourceId]: err }));
        },
      });
    })();

    return () => {
      cancelled = true;
      if (closeActivity) closeActivity();
      if (closeUsers) closeUsers();
    };
  }, [companyId, isOwner, epoch]);

  // Recolour every minute from the data already loaded (0 reads).
  useEffect(() => {
    const t = setInterval(() => setNowMs(Date.now()), 60 * 1000);
    return () => clearInterval(t);
  }, []);

  const result = useMemo(() => {
    if (!users) return null;
    const actions = collectActions(docsBySource, windowStart(nowMs));
    return buildActivityRows({ users, actions, nowMs, lang });
  }, [users, docsBySource, nowMs, lang]);

  const legend = useMemo(
    () => (result ? legendFor(result.rows.map((r) => r.user.role), lang) : []),
    [result, lang]
  );

  // v1.1: a COMPLETE live result (users + every source read, none failed, none
  // at its limit) refreshes the Home card's 30-minute cache — Home then reads 0.
  const complete = !!result && !usersError && !claimMissing
    && ACTIVITY_SOURCES.every((s) => docsBySource[s.id] && !errors[s.id])
    && !Object.values(sizes).some(limitHit);
  // Commit 7: the score counts without the Owner looking — the same as Home's.
  useEffect(() => {
    if (!isOwner || !complete || !result) return;
    const viewer = { uid: viewerUid, docId: viewerDocId };
    writeCountsCache(companyId, viewer, Date.now(), scoreCounts(result, viewer));
  }, [isOwner, complete, result, companyId, viewerUid, viewerDocId]);

  if (!isOwner) {
    return (
      <div className="p-4 md:p-6 max-w-3xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-xl p-5 text-sm text-red-700">
          {tx("ownerOnly")}
        </div>
      </div>
    );
  }

  const failed = Object.keys(errors);
  const capped = Object.entries(sizes).filter(([, n]) => limitHit(n)).map(([id]) => id);
  const reported = ACTIVITY_SOURCES.filter((s) => docsBySource[s.id] || errors[s.id]).length;
  const loading = !claimMissing && !usersError && (!users || reported < ACTIVITY_SOURCES.length);
  const view = filterView(result, filter, { uid: viewerUid, docId: viewerDocId });
  const tapChip = (id) => setFilter((cur) => toggleFilter(cur, id));

  // Amber / red rows get a WhatsApp nudge; green rows do not.
  const rowNudge = (row) => {
    if (row.band === BAND.GREEN) return null;
    return safeNudge({
      phone:       contactPhoneOf(row.user), // 8b: WhatsApp number first, else the login phone
      kind:        NUDGE_KIND.SLOW,
      role:        row.user.role,
      name:        row.user.name,
      workingDays: row.last ? workingDaysAgo(row.last.atMs, nowMs) : null,
      ...sender,
    });
  };
  // Amber / red row whose user has no valid WhatsApp / login mobile → the no-phone hint (never on green rows).
  const rowPhoneHint = (row) => (row.band === BAND.GREEN || contactPhoneOf(row.user) ? null : noPhoneHint(row.user));

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div className="min-w-0">
          <h1 className="text-xl font-bold text-gray-900">{tx("title")}</h1>
          <p className="text-sm text-gray-500 mt-0.5">{tx("subtitle", { days: WINDOW_DAYS })}</p>
          <p className="text-xs text-gray-400 mt-0.5">
            {tx("live")}{lastChangeAt ? ` · ${tx("lastChange", { time: hhmm(lastChangeAt) })}` : ""}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <LangToggle defaultLang={PAGE_DEFAULT_LANG} />
          <button
            type="button"
            onClick={() => setEpoch((e) => e + 1)}
            className="px-4 py-2 text-sm font-semibold rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white whitespace-nowrap"
          >
            {tx("refresh")}
          </button>
        </div>
      </div>

      {/* Banners */}
      {claimMissing && (
        <div className="mb-3 bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700">{tx("claimMissing")}</div>
      )}
      {usersError && (
        <div className="mb-3 bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700">
          {tx("usersError", { reason: tx(errorKey(usersError)) })}
        </div>
      )}
      {failed.length > 0 && (
        <div className="mb-3 bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700">
          <p className="font-semibold">{tx("failedTitle")}</p>
          <ul className="list-disc ml-5 mt-1">
            {failed.map((id) => <li key={id}>{SOURCE_LABELS[id] || id}: {tx(errorKey(errors[id]))}</li>)}
          </ul>
          <p className="mt-1">{tx("failedRetry")}</p>
        </div>
      )}
      {capped.length > 0 && (
        <div className="mb-3 bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-800">
          {tx("capped", { limit: QUERY_LIMIT, days: WINDOW_DAYS, sources: capped.map((id) => SOURCE_LABELS[id] || id).join(", ") })}
        </div>
      )}

      {loading && <p className="text-sm text-gray-500">{tx("loading")}</p>}

      {result && !loading && (
        <>
          {/* Summary chips = filters (tap = only that group, tap again = all) */}
          <div className="flex flex-wrap gap-2 mb-2">
            {[BAND.RED, BAND.AMBER, BAND.GREEN].map((b) => (
              <FilterChip key={b} id={b} label={groupLabel(b, lang)} count={result.counts[b]}
                selected={filter === b} onTap={tapChip} />
            ))}
            <FilterChip id="never" label={groupLabel("never", lang)} count={result.counts.neverLoggedIn}
              selected={filter === "never"} onTap={tapChip} />
            {(result.counts.inactive > 0 || filter === "inactive") && (
              <FilterChip id="inactive" label={groupLabel("inactive", lang)} count={result.counts.inactive}
                selected={filter === "inactive"} onTap={tapChip} />
            )}
          </div>

          {filter && (
            <p className="text-xs text-gray-600 mb-2">
              {tx("showing", { group: groupLabel(filter, lang) })} ·{" "}
              <button type="button" onClick={() => setFilter(null)} className="font-semibold text-indigo-700 hover:underline">
                {tx("showAll")}
              </button>
            </p>
          )}

          {/* Legend — only the roles on this page */}
          <div className="text-xs text-gray-500 mb-4 space-y-0.5">
            {legend.map((l) => (
              <p key={l.roles.join("|")}>
                <span className="font-semibold text-gray-700">{l.roles.map(roleLabel).join(" / ")}:</span> {l.text}
              </p>
            ))}
            <p>{tx("legendSundays", { days: WINDOW_DAYS, red: groupLabel(BAND.RED, lang) })}</p>
            <p>{tx("legendNotCounted")}</p>
          </div>

          {view.empty && (
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-sm text-gray-600">{tx("nobodyHere")}</div>
          )}

          {/* Rows */}
          {view.showRows && (view.rows.length === 0 ? (
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-sm text-gray-600">{tx("noActiveUsers")}</div>
          ) : (
            <div className="space-y-2">
              {view.rows.map((row) => {
                const k = row.user.docId || row.user.uid;
                return (
                  <UserRow
                    key={k}
                    row={row}
                    nowMs={nowMs}
                    open={openKey === k}
                    onToggle={() => setOpenKey(openKey === k ? null : k)}
                    nudge={rowNudge(row)}
                    phoneHint={rowPhoneHint(row)}
                    lang={lang}
                    tx={tx}
                  />
                );
              })}
            </div>
          ))}

          {/* Never logged in */}
          {view.showNever && (
            <details key={`never-${view.openNever}`} open={view.openNever} className="mt-5 bg-white border border-gray-200 rounded-xl p-3 sm:p-4">
              <summary className="text-sm font-semibold text-gray-700 cursor-pointer">
                {groupLabel("never", lang)} ({result.neverLoggedIn.length})
              </summary>
              <p className="text-xs text-gray-500 mt-1 mb-2">{tx("neverDesc")}</p>
              <ul className="space-y-2">
                {result.neverLoggedIn.map(({ user: u }) => {
                  const contact = contactPhoneOf(u); // 8b: WhatsApp number first, else the login phone
                  const hasMobile = contact !== "";
                  const nudge = hasMobile ? safeNudge({
                    phone: contact, kind: NUDGE_KIND.NEVER_LOGGED_IN, role: u.role, name: u.name, appUrl, ...sender,
                  }) : null;
                  return (
                    <li key={u.docId} className="text-sm text-gray-700 flex flex-wrap items-center justify-between gap-2">
                      <span className="flex flex-wrap gap-x-2 min-w-0">
                        <span className="break-words">{u.name || tx("noName")}</span>
                        <span className="text-xs text-gray-500">{roleLabel(u.role)}</span>
                        {(contact || u.phone) ? <span className="text-xs text-gray-400">{contact || u.phone}</span> : null}
                      </span>
                      {hasMobile
                        ? <WhatsAppButton nudge={nudge} name={u.name} tx={tx} />
                        : <NoPhoneHint hint={noPhoneHint(u)} tx={tx} />}
                    </li>
                  );
                })}
              </ul>
            </details>
          )}

          {/* Inactive users — switched off; no colour, no nudge */}
          {view.showInactive && (
            <details key={`inactive-${view.openInactive}`} open={view.openInactive} className="mt-3 bg-gray-50 border border-gray-200 rounded-xl p-3 sm:p-4 text-gray-500">
              <summary className="text-sm font-semibold cursor-pointer">{groupLabel("inactive", lang)} ({result.inactive.length})</summary>
              <p className="text-xs mt-1 mb-2">{tx("inactiveDesc")}</p>
              <ul className="space-y-1.5">
                {result.inactive.map(({ user: u, last }) => (
                  <li key={u.docId} className="text-sm flex flex-wrap gap-x-2">
                    <span className="break-words">{u.name || tx("noName")}</span>
                    <span className="text-xs">{roleLabel(u.role)}</span>
                    <span className="text-xs">
                      {last ? <>{last.label} · {whenText(last.atMs, nowMs, lang)}</> : tx("noActionInDays", { days: WINDOW_DAYS })}
                    </span>
                  </li>
                ))}
              </ul>
            </details>
          )}

          {/* Unmatched footnote */}
          {view.showUnmatched && (
            <details className="mt-3 text-xs text-gray-500">
              <summary className="cursor-pointer">
                {result.unmatched.length === 1 ? tx("unmatchedOne") : tx("unmatchedMany", { count: result.unmatched.length })}
              </summary>
              <p className="mt-1">{tx("unmatchedDesc")}</p>
              <ul className="mt-1 space-y-1">
                {result.unmatched.slice(0, 20).map((a) => (
                  <li key={a.key} className="flex flex-wrap gap-x-2">
                    <span className="whitespace-nowrap">{dateTime(a.atMs)}</span>
                    <ActionLine a={a} />
                    <span>{tx("by", { who: a.whoName || a.who })}</span>
                  </li>
                ))}
              </ul>
              {result.unmatched.length > 20 && <p className="mt-1">{tx("andMore", { count: result.unmatched.length - 20 })}</p>}
            </details>
          )}
        </>
      )}
    </div>
  );
}
